import { Button, SelectOptionElement } from '@odin-labs/components';
import {
  useGetJobsiteConfigSiteLayoutQuery,
  useGetSiteLayoutConnectionsQuery,
  useGetSiteLayoutPublicationsQuery,
  useGetSiteReaderDevicesQuery,
  useReconcileSiteLayoutMutation,
  ZonesMode,
} from 'apollo/generated/client-operations';
import { AlertService } from 'components/alertService';
import { LoadingError } from 'components/loadingError';
import { JobsiteConfigTabProps } from 'containers/jobsiteConfiguration/types';
import moment from 'moment-timezone';
import React from 'react';
import {
  diffLayouts,
  draftTagOf,
  effectiveRevision,
  entriesFacingZone,
  entryJoinLabel,
  EntrySide,
  Layout,
  publishChecks,
  SiteLayoutError,
  siteReadersOf,
  useSiteLayout,
} from 'siteLayout';
import { useIsMounted } from 'utils';
import { pluralize } from 'utils/pluralize';
import {
  AvigilonStatus,
  AvigilonSyncStatus,
  avigilonEntrySideLine,
  avigilonZoneLine,
  type ConnectionRead,
  connectionReadsOf,
  deliveryOutstanding,
  deliveryRunning,
  draftEntrySideLine,
  draftZoneLine,
  EntryEditor,
  LayoutEntityCard,
  LayoutHeader,
  LayoutSidebar,
  OUTSIDE_OPTION_VALUE,
  type PublicationRead,
  publicationReadOf,
  RevisionBanner,
  SideReader,
  ZoneEditor,
  type ZonesModeRead,
} from './components';
import { draftTagBadge } from './labels';
import { DiscardDraftModal, PublishDraftModal } from './modals';
import { useLayoutSelection } from './useLayoutSelection';

type OpenModal = 'publish' | 'discard' | null;

const EMPTY_LAYOUT: Layout = { zones: [], entries: [], readerAssignments: [] };

const REVISION_VALUE_PREFIX = 'rev:';
const DRAFT_VALUE = 'draft';
const NO_LAYOUT_VALUE = 'none';

/** How often delivery's status is read while delivery is outstanding. */
const POLL_INTERVAL_MS = 2000;
/**
 * Failed reads of delivery's status in a row, whatever made them, after
 * which the panel stops reading it: polling stops, and so do the reads of
 * an attempt due but not yet claimed.
 */
const MAX_FAILED_READS = 5;
/** Longest wait between reads of an attempt that is due but not yet claimed. */
const MAX_OVERDUE_READ_WAIT_MS = 30_000;
/** The wait before reading an attempt due but not yet claimed again, after `reads` reads of it. */
const overdueReadWait = (reads: number): number =>
  Math.min(POLL_INTERVAL_MS * 2 ** reads, MAX_OVERDUE_READ_WAIT_MS);

const monthDay = (iso: string | null): string =>
  iso ? moment(iso).format('MMM D') : '—';

const classes = {
  container:
    'odin-flex odin-h-full odin-min-h-0 odin-flex-col odin-gap-4 odin-p-6',
  body: 'odin-flex odin-min-h-0 odin-flex-1 odin-gap-4',
  main: 'odin-min-w-0 odin-flex-1 odin-overflow-y-auto odin-rounded-lg odin-border odin-border-gray-200 odin-bg-white odin-p-5',
  empty: 'odin-py-10 odin-text-sm odin-text-gray-500',
  emptyLayout: 'odin-flex odin-flex-col odin-items-start odin-gap-3 odin-py-10',
};

export function SiteLayoutTab({
  jobsiteId,
  onTabApiChange,
  onIsDirtyChange,
}: JobsiteConfigTabProps): React.ReactElement {
  const {
    data: jobsiteData,
    loading: jobsiteLoading,
    refetch: refetchJobsite,
  } = useGetJobsiteConfigSiteLayoutQuery({
    variables: { jobsiteId },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });
  const jobsiteName = jobsiteData?.getJobsite?.name;

  const {
    state,
    loading: layoutLoading,
    error: layoutError,
    refetch: refetchLayout,
    actions,
    stalled,
  } = useSiteLayout(jobsiteId);
  const {
    data: readerDevicesData,
    error: readerDevicesError,
    refetch: refetchReaderDevices,
  } = useGetSiteReaderDevicesQuery({
    variables: { jobsiteId },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });
  // Read apart from the layout, with errorPolicy 'all', so one zone's
  // ambiguous Avigilon zone fails that zone's status alone.
  const {
    data: connectionsData,
    error: connectionsError,
    refetch: refetchConnections,
  } = useGetSiteLayoutConnectionsQuery({
    variables: { siteId: jobsiteId },
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
  });
  const {
    data: publicationsData,
    error: publicationsError,
    loading: publicationsLoading,
    refetch: refetchPublications,
    startPolling: startPollingPublications,
    stopPolling: stopPollingPublications,
  } = useGetSiteLayoutPublicationsQuery({
    variables: { siteId: jobsiteId },
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
  });
  const [reconcileSiteLayout] = useReconcileSiteLayoutMutation();
  // A Sync now answered after the site changed reports nothing.
  const currentSiteId = React.useRef(jobsiteId);
  currentSiteId.current = jobsiteId;

  const { selection, select, clear } = useLayoutSelection();
  const [viewRevisionId, setViewRevisionId] = React.useState<string | null>(
    null,
  );
  const [pickerSideId, setPickerSideId] = React.useState<string | null>(null);
  const [openModal, setOpenModal] = React.useState<OpenModal>(null);
  const [busy, setBusy] = React.useState(false);
  const running = React.useRef(false);

  // Delivery runs in the background: what Avigilon has is read again after
  // a publish, when the reads disagree with the effective revision, when
  // delivery's run changes, and after each Sync now, whatever its outcome.
  // While such reads are in flight, a disagreement waits for all of them;
  // the round is bumped once the last one settles. Each also resumes
  // reading that gave up on failed reads.
  const isMounted = useIsMounted();
  const avigilonInFlight = React.useRef(0);
  const [avigilonRound, setAvigilonRound] = React.useState(0);
  // Failed reads of delivery's status in a row, whoever made them. Polling
  // and reading an attempt not yet claimed both stop at MAX_FAILED_READS.
  const [failedReads, setFailedReads] = React.useState(0);
  const readingGaveUp = failedReads >= MAX_FAILED_READS;
  // The attempt the last loaded read found still to come and not yet
  // claimed, and how many times it was read again once it was due.
  const retryReads = React.useRef<{ dueAt: string; reads: number } | null>(
    null,
  );
  const resumeReading = React.useCallback((): void => setFailedReads(0), []);
  const readAvigilonAgain = React.useCallback(
    (alsoLayout: boolean): Promise<void> => {
      avigilonInFlight.current += 1;
      resumeReading();
      return Promise.all([
        alsoLayout ? refetchLayout() : undefined,
        refetchConnections().catch((): undefined => undefined),
        refetchPublications().catch((): undefined => undefined),
      ]).then((): void => {
        avigilonInFlight.current -= 1;
        if (!avigilonInFlight.current && isMounted())
          setAvigilonRound((round) => round + 1);
      });
    },
    [
      refetchLayout,
      refetchConnections,
      refetchPublications,
      isMounted,
      resumeReading,
    ],
  );

  const refetchData = React.useCallback((): void => {
    refetchJobsite();
    refetchLayout();
    refetchReaderDevices();
    readAvigilonAgain(false);
  }, [refetchJobsite, refetchLayout, refetchReaderDevices, readAvigilonAgain]);

  React.useEffect(
    () =>
      onTabApiChange?.({
        refetchData,
        loading: jobsiteLoading || layoutLoading,
        jobsiteName,
      }),
    [onTabApiChange, refetchData, jobsiteLoading, layoutLoading, jobsiteName],
  );
  // The draft saves as it is edited; the page-level Save Changes stays disabled.
  React.useEffect(() => onIsDirtyChange(false), [onIsDirtyChange]);

  // ---- derived view of the layout
  const revisions = state?.revisions ?? [];
  const draft = state?.draft ?? null;
  const effective = effectiveRevision(revisions);
  const viewingRevision = viewRevisionId
    ? revisions.find((revision) => revision.id === viewRevisionId) ?? null
    : null;
  const layout: Layout = viewingRevision ?? draft ?? effective ?? EMPTY_LAYOUT;
  const isDraft = draft !== null;
  const editingDraft = isDraft && !viewingRevision;
  // A stalled command leaves the layout read-only until the page is reloaded.
  const readOnly = !editingDraft || busy || stalled;

  // Until the site's reader devices load, its readers are unknown, not none:
  // the placed readers are labelled as such and the reader checks do not run.
  const readersUnknownLabel = readerDevicesError
    ? 'Could not load readers'
    : 'Loading readers…';
  const siteReaders = React.useMemo(
    () => readerDevicesData && siteReadersOf(readerDevicesData),
    [readerDevicesData],
  );
  const placeableReaders = siteReaders?.filter(({ missing }) => !missing);
  const readersById = React.useMemo(
    () =>
      new Map<string, SideReader>(
        siteReaders
          ? siteReaders.map((reader) => [reader.id, reader])
          : layout.readerAssignments.map(({ readerDeviceId }) => [
              readerDeviceId,
              {
                id: readerDeviceId,
                name: 'Reader',
                portLabel: readersUnknownLabel,
                missing: true,
              },
            ]),
      ),
    [siteReaders, layout.readerAssignments, readersUnknownLabel],
  );
  const readerNameOf = (readerDeviceId: string): string =>
    readersById.get(readerDeviceId)?.name ?? 'Unknown reader';
  const placedIds = new Set(
    layout.readerAssignments.map((assignment) => assignment.readerDeviceId),
  );
  const unplacedCount = (placeableReaders ?? []).filter(
    (reader) => !placedIds.has(reader.id),
  ).length;
  // The readers on the site include those Avigilon no longer lists for it,
  // which stay on the site; not placed counts only those that can still be
  // placed. Those not connected to Avigilon are counted only when there are any.
  const notConnectedCount = (siteReaders ?? []).filter(
    ({ notConnected }) => notConnected,
  ).length;
  let readerStripText = readersUnknownLabel;
  if (siteReaders)
    readerStripText = siteReaders.length
      ? `${pluralize(
          siteReaders.length,
          'reader',
        )} on this site, ${unplacedCount} not placed${
          notConnectedCount
            ? `, ${notConnectedCount} not connected to Avigilon`
            : ''
        }`
      : 'No readers on this site yet. Go to Site Settings > Devices and select Add controller to bring its readers onto this site.';

  // ---- what Avigilon has of the effective layout
  const effectiveId = effective?.id ?? null;
  const connectionReads = connectionReadsOf(
    jobsiteId,
    effectiveId,
    connectionsData,
    connectionsError,
  );
  // A read in flight is neither loaded nor failed. Apollo renders it as
  // loading with no error and, after a read the network failed, with the
  // answer from before that failure. What the last read to settle answered
  // is used instead: each read that settles is a new value here, so what
  // depends on it runs once for each read.
  const [settledPublications, setSettledPublications] = React.useState<{
    siteId: string;
    data: typeof publicationsData;
    error: typeof publicationsError;
  } | null>(null);
  const staleReadAgainFor = React.useRef<string | null>(null);
  const [staleGivenUpFor, setStaleGivenUpFor] = React.useState<string | null>(
    null,
  );
  // What was read, read again, given up on, or polled belongs to one visit
  // to a site: coming back to it reads afresh, and shows loading until a
  // read of this visit settles. The kept read is labelled with its site, so
  // another site's read is never used, and the render where the site
  // changes is loading under no-cache, so nothing is kept in it. This runs
  // before the effect that keeps the settled read: were the read ever
  // answered from a cache, that effect could keep the new site's read in
  // the same render, and clearing after it would discard that read.
  React.useEffect(() => {
    setSettledPublications(null);
    staleReadAgainFor.current = null;
    setStaleGivenUpFor(null);
    setFailedReads(0);
    retryReads.current = null;
  }, [jobsiteId]);
  React.useEffect(() => {
    if (!publicationsLoading)
      setSettledPublications({
        siteId: jobsiteId,
        data: publicationsData,
        error: publicationsError,
      });
  }, [publicationsLoading, publicationsData, publicationsError, jobsiteId]);
  const settledRead =
    settledPublications?.siteId === jobsiteId ? settledPublications : null;
  const publicationRead = publicationReadOf(
    jobsiteId,
    effectiveId,
    settledRead?.data,
    settledRead?.error,
  );
  // Null while unknown, which is never taken for on or off.
  const zonesMode =
    publicationRead.state === 'loaded' ? publicationRead.zonesMode : null;
  const zonesOff = zonesMode === ZonesMode.Off;

  // A read of another revision than the designer's effective one (after a
  // publish here or elsewhere) is not used. Each distinct disagreement has
  // the layout and both reads read again once; reading the layout again
  // brings the designer's effective revision forward. One that outlasts its
  // read again is shown as a failed read rather than loading forever.
  let staleKey: string | null = null;
  if (connectionReads.stale || publicationRead.state === 'stale')
    staleKey = [
      effectiveId ?? '',
      connectionReads.stale ? connectionReads.stale.revisionId ?? 'none' : '',
      publicationRead.state === 'stale'
        ? publicationRead.revisionIds.join(',')
        : '',
    ].join('|');
  React.useEffect(() => {
    if (!staleKey || !state || layoutLoading || avigilonInFlight.current)
      return;
    if (staleReadAgainFor.current === staleKey) {
      setStaleGivenUpFor(staleKey);
      return;
    }
    staleReadAgainFor.current = staleKey;
    readAvigilonAgain(true);
  }, [staleKey, state, layoutLoading, avigilonRound, readAvigilonAgain]);
  const staleGivenUp = staleKey !== null && staleGivenUpFor === staleKey;
  const connectionReadOf = (id: string): ConnectionRead =>
    staleGivenUp && connectionReads.stale
      ? { state: 'failed' }
      : connectionReads.of(id);
  const shownPublicationRead: PublicationRead =
    staleGivenUp && publicationRead.state === 'stale'
      ? { state: 'failed' }
      : publicationRead;
  let zonesModeRead: ZonesModeRead = 'loading';
  if (shownPublicationRead.state === 'loaded')
    zonesModeRead = shownPublicationRead.zonesMode;
  else if (shownPublicationRead.state === 'failed') zonesModeRead = 'failed';

  // While a delivery run is under way, its status is polled; it stops once a
  // read loads and no run is under way, when the site changes, or when the
  // tab goes. A read that fails or disagrees keeps the polling going, up to
  // a point: it stops once a disagreement is given up on, or after
  // MAX_FAILED_READS failed reads in a row, leaving the failure shown until
  // Sync now or the page reads again.
  const lastLoadedPublication = React.useRef<{
    siteId: string;
    running: boolean;
  } | null>(null);
  if (publicationRead.state === 'loaded')
    lastLoadedPublication.current = {
      siteId: jobsiteId,
      running: deliveryRunning(publicationRead),
    };
  // Delivery still to come but not under way, such as a retry after a
  // failure, is read once, a poll interval after its next attempt falls due,
  // when delivery has claimed it: a delivery that keeps failing is read at
  // its own retry pace, not polled. Delivery may not have claimed it by then
  // (it runs one request at a time, so a slow site delays the others, and
  // this clock may be ahead of the server's): each read that still finds the
  // same attempt due and unclaimed is read again after a wait that doubles,
  // from a poll interval up to MAX_OVERDUE_READ_WAIT_MS. A read that fails
  // meanwhile, before the attempt falls due or after, is followed by the
  // read that would have come next, the one at the due time or the next on
  // the backoff, until MAX_FAILED_READS reads fail in a row.
  const nextAttemptDueAt =
    publicationRead.state === 'loaded' &&
    deliveryOutstanding(publicationRead) &&
    !deliveryRunning(publicationRead)
      ? publicationRead.publication?.nextAttemptAt ?? null
      : null;
  const publicationState = publicationRead.state;
  // Each read that settles arms the next read, even one that answers as the
  // last did, so the settled read is a dependency.
  React.useEffect(() => {
    if (nextAttemptDueAt)
      retryReads.current = {
        dueAt: nextAttemptDueAt,
        reads:
          retryReads.current?.dueAt === nextAttemptDueAt
            ? retryReads.current.reads
            : 0,
      };
    else if (publicationState === 'loaded') retryReads.current = null;
    const attempt = retryReads.current;
    if (
      !attempt ||
      readingGaveUp ||
      (!nextAttemptDueAt && publicationState !== 'failed')
    )
      return undefined;
    const untilDue = moment(attempt.dueAt).valueOf() - Date.now();
    // Only a read made once the attempt is due lengthens the next wait; the
    // read at the due time does not.
    const due = untilDue <= 0;
    const timer = setTimeout(
      () => {
        if (due) retryReads.current = { ...attempt, reads: attempt.reads + 1 };
        refetchPublications().catch((): undefined => undefined);
      },
      due ? overdueReadWait(attempt.reads) : untilDue + POLL_INTERVAL_MS,
    );
    return (): void => clearTimeout(timer);
  }, [
    settledRead,
    nextAttemptDueAt,
    publicationState,
    readingGaveUp,
    refetchPublications,
  ]);
  const publicationLoaded = publicationRead.state === 'loaded';
  // Each read counts once it settles: a read that fails adds to the failed
  // reads in a row, and one that loads starts them again.
  React.useEffect(() => {
    if (publicationState === 'failed') setFailedReads((reads) => reads + 1);
    else if (publicationState === 'loaded') setFailedReads(0);
  }, [settledRead, publicationState]);
  // A Sync now that queued delivery leaves it outstanding until a read after
  // it loads, so a first read that fails is polled again rather than leaving
  // the panel failed while delivery runs.
  // The latest settled read when the sync is requested, not the one when
  // Sync now was pressed: a read that lands during the request is no read
  // after it. The settled read is compared with it, as it is the one that
  // says whether the read loaded.
  const settledData = settledRead?.data;
  const latestPublicationsData = React.useRef(settledData);
  latestPublicationsData.current = settledData;
  const [syncRequested, setSyncRequested] = React.useState<{
    siteId: string;
    data: typeof publicationsData;
  } | null>(null);
  React.useEffect(() => {
    if (
      syncRequested &&
      publicationLoaded &&
      settledData !== syncRequested.data
    )
      setSyncRequested(null);
  }, [syncRequested, publicationLoaded, settledData]);
  const polling =
    ((lastLoadedPublication.current?.siteId === jobsiteId &&
      lastLoadedPublication.current.running) ||
      syncRequested?.siteId === jobsiteId) &&
    !staleGivenUp &&
    !readingGaveUp;
  React.useEffect(() => {
    if (polling) startPollingPublications(POLL_INTERVAL_MS);
    else stopPollingPublications();
    return stopPollingPublications;
  }, [polling, startPollingPublications, stopPollingPublications]);

  // A new or finished run may have created zones and entries in Avigilon.
  const latestRun =
    publicationRead.state === 'loaded'
      ? publicationRead.publication?.run ?? null
      : null;
  const runKey = latestRun
    ? `${latestRun.id}|${latestRun.status}|${latestRun.finishedAt ?? ''}`
    : null;
  const seenRun = React.useRef<{ siteId: string; key: string } | null>(null);
  React.useEffect(() => {
    if (!runKey) return;
    const seen = seenRun.current;
    seenRun.current = { siteId: jobsiteId, key: runKey };
    if (seen && seen.siteId === jobsiteId && seen.key !== runKey)
      refetchConnections().catch((): undefined => undefined);
  }, [runKey, jobsiteId, refetchConnections]);

  // ---- commands, each reported through the alert service; answers whether the command went through
  const run = async (
    work: () => Promise<void>,
    success?: string,
  ): Promise<boolean> => {
    // A stalled command is already reported by the header's notice.
    if (stalled) return false;
    if (running.current) {
      AlertService.alert(
        'warn',
        'Layout',
        'Another layout change is still saving. Try again when it finishes.',
      );
      return false;
    }
    running.current = true;
    setBusy(true);
    try {
      await work();
      if (success) AlertService.alert('success', 'Layout', success);
      return true;
    } catch (error) {
      const message =
        error instanceof SiteLayoutError
          ? error.message
          : (error as Error).message;
      AlertService.alert('danger', 'Something went wrong!', message);
      return false;
    } finally {
      running.current = false;
      setBusy(false);
    }
  };

  const editLayout = (): void => {
    run(
      async () => {
        await actions.openDraft();
        setViewRevisionId(null);
      },
      effective
        ? `Draft opened from revision ${effective.number}. Nothing changes on site until you publish.`
        : undefined,
    );
  };

  const addZone = (): void => {
    run(async () => {
      const zone = await actions.addZone();
      setViewRevisionId(null);
      select('zone', zone.id);
    });
  };

  const addEntry = (): void => {
    run(async () => {
      const entry = await actions.addEntry();
      setViewRevisionId(null);
      setPickerSideId(null);
      select('entry', entry.id);
    });
  };

  const setFaces = (entryId: string, side: EntrySide, value: string): void => {
    run(async (): Promise<void> => {
      await actions.updateEntrySide(
        entryId,
        side.id,
        value === OUTSIDE_OPTION_VALUE
          ? { kind: 'OUTSIDE' }
          : { kind: 'ZONE', zoneId: value },
      );
    });
  };

  const placeReader = (
    entryId: string,
    side: EntrySide,
    readerDeviceId: string,
  ): void => {
    run(async () => {
      await actions.assignReader({
        readerDeviceId,
        entryId,
        admitsIntoSideId: side.id,
      });
      setPickerSideId(null);
    }, `${readerNameOf(readerDeviceId)} placed. Publish to make it effective.`);
  };

  const removeZone = (zoneId: string): void => {
    run(async () => {
      await actions.removeZone(zoneId);
      clear();
    });
  };

  const removeEntry = (entryId: string, entryName: string): void => {
    const readerCount = layout.readerAssignments.filter(
      (assignment) => assignment.entryId === entryId,
    ).length;
    run(async () => {
      await actions.removeEntry(entryId);
      clear();
    }, `${entryName || 'Entry'} removed from the draft.${readerCount ? ` ${pluralize(readerCount, 'reader')} removed from this draft.` : ''}`);
  };

  const publish = (): void => {
    const number = (effective?.number ?? 0) + 1;
    run(async () => {
      await actions.publishDraft();
      // Read directly: a read with no earlier delivery cannot show itself stale.
      readAvigilonAgain(false);
      setOpenModal(null);
      setViewRevisionId(null);
      clear();
    }, `Revision ${number} published.`);
  };

  const discard = (): void => {
    run(async () => {
      await actions.discardDraft();
      setOpenModal(null);
      setViewRevisionId(null);
      clear();
    }, 'Draft discarded.');
  };

  const startDraftFrom = (revisionId: string, number: number): void => {
    run(async () => {
      await actions.openDraft(revisionId);
      setViewRevisionId(null);
      clear();
    }, `Draft started from revision ${number}. This is a copy, not a rollback; the current layout stays effective until you publish.`);
  };

  // Requests delivery of the effective layout to the site's connectors again.
  const syncNow = (): void => {
    const siteId = jobsiteId;
    const isCurrentSite = (): boolean => siteId === currentSiteId.current;
    run(async () => {
      try {
        const { data } = await reconcileSiteLayout({
          variables: { input: { siteId } },
        });
        if (!isCurrentSite()) return;
        if (!data?.reconcileSiteLayout.connectorCount) {
          AlertService.alert(
            'warn',
            'Avigilon',
            'This site has no enabled connector, so there is nothing to sync.',
          );
        } else {
          setSyncRequested({
            siteId,
            data: latestPublicationsData.current,
          });
          AlertService.alert(
            'success',
            'Avigilon',
            zonesOff
              ? 'Sync requested. Zones are off for this site, so nothing is written to Avigilon until zones are enabled.'
              : 'Sync requested.',
          );
        }
      } catch (error) {
        if (isCurrentSite()) throw error;
      } finally {
        // The zones and entry sides are read again once the run changes.
        if (isCurrentSite()) {
          resumeReading();
          refetchPublications().catch((): undefined => undefined);
        }
      }
    });
  };

  // ---- revision selector and banner
  const revisionOptions: SelectOptionElement[] = [];
  if (isDraft) {
    revisionOptions.push({
      value: DRAFT_VALUE,
      label: 'Draft · unpublished changes',
    });
  }
  [...revisions].reverse().forEach((revision, index) => {
    revisionOptions.push({
      value: `${REVISION_VALUE_PREFIX}${revision.id}`,
      label:
        index === 0
          ? `Rev ${revision.number} · current, effective ${monthDay(
              revision.publishedAt,
            )}`
          : `Rev ${revision.number} · ${monthDay(
              revision.publishedAt,
            )} to ${monthDay(revision.closedAt)}`,
    });
  });
  if (!revisionOptions.length) {
    revisionOptions.push({ value: NO_LAYOUT_VALUE, label: 'No layout yet' });
  }
  let revisionValueKey = NO_LAYOUT_VALUE;
  if (viewingRevision)
    revisionValueKey = `${REVISION_VALUE_PREFIX}${viewingRevision.id}`;
  else if (isDraft) revisionValueKey = DRAFT_VALUE;
  else if (effective)
    revisionValueKey = `${REVISION_VALUE_PREFIX}${effective.id}`;
  const revisionValue =
    revisionOptions.find((option) => option.value === revisionValueKey) ?? null;
  const onRevisionChange = (value: string): void => {
    clear();
    if (value.startsWith(REVISION_VALUE_PREFIX)) {
      const id = value.slice(REVISION_VALUE_PREFIX.length);
      setViewRevisionId(!isDraft && effective?.id === id ? null : id);
    } else {
      setViewRevisionId(null);
    }
  };

  let banner: RevisionBanner | null = null;
  if (viewingRevision) {
    const isCurrent = effective?.id === viewingRevision.id;
    const index = revisions.findIndex(
      (revision) => revision.id === viewingRevision.id,
    );
    const previous = index > 0 ? revisions[index - 1] : null;
    banner = {
      text: isCurrent
        ? `You are viewing Rev ${
            viewingRevision.number
          }, the current layout, effective since ${monthDay(
            viewingRevision.publishedAt,
          )}. Your draft has unpublished changes.`
        : `You are viewing Rev ${viewingRevision.number}, effective ${monthDay(
            viewingRevision.publishedAt,
          )} to ${monthDay(
            viewingRevision.closedAt,
          )}. The current layout is Rev ${effective?.number ?? '—'}.`,
      changes: diffLayouts(previous, viewingRevision),
      currentLabel: isDraft ? 'Back to draft' : 'View current',
      onViewCurrent: (): void => {
        setViewRevisionId(null);
        clear();
      },
      canStartDraft: !isDraft && !isCurrent,
      onStartDraft: (): void =>
        startDraftFrom(viewingRevision.id, viewingRevision.number),
    };
  }

  // ---- sidebar contents
  const canAddZone = !viewingRevision && (editingDraft || !effective);
  const canAddEntry = editingDraft && layout.zones.length > 0;
  const removedNames =
    editingDraft && effective
      ? [
          ...effective.zones.filter(
            (zone) =>
              !layout.zones.some((candidate) => candidate.id === zone.id),
          ),
          ...effective.entries.filter(
            (entry) =>
              !layout.entries.some((candidate) => candidate.id === entry.id),
          ),
        ].map((item) => item.name)
      : [];

  const selectedZone =
    selection?.type === 'zone'
      ? layout.zones.find((zone) => zone.id === selection.id)
      : undefined;
  const selectedEntry =
    selection?.type === 'entry'
      ? layout.entries.find((entry) => entry.id === selection.id)
      : undefined;
  const isEmptyLayout =
    editingDraft && !layout.zones.length && !layout.entries.length;

  // Only the effective layout is in Avigilon. A zone or entry it has shows
  // its status there, in the draft too (ids are stable across revisions),
  // labelled as the published state. One new in the draft says when it
  // would be created. Older revisions show nothing.
  const viewingOlderRevision =
    viewingRevision !== null && viewingRevision.id !== effective?.id;
  const publishedNote =
    editingDraft && effective
      ? `Published layout, Rev ${effective.number}. Draft changes are not in Avigilon.`
      : undefined;
  const readerAdmitsInto = (from: Layout, side: EntrySide): boolean =>
    from.readerAssignments.some(
      (assignment) => assignment.admitsIntoSideId === side.id,
    );
  let zoneAvigilonStatus: React.ReactNode = null;
  if (selectedZone && !viewingOlderRevision) {
    if (effective?.zones.some((zone) => zone.id === selectedZone.id)) {
      zoneAvigilonStatus = (
        <AvigilonStatus
          title="Avigilon zone"
          note={publishedNote}
          lines={[avigilonZoneLine(connectionReadOf(selectedZone.id))]}
        />
      );
    } else if (editingDraft) {
      zoneAvigilonStatus = (
        <AvigilonStatus
          title="Avigilon zone"
          lines={[draftZoneLine(zonesModeRead)]}
        />
      );
    }
  }
  let entryAvigilonStatus: React.ReactNode = null;
  if (selectedEntry && !viewingOlderRevision) {
    const published = effective?.entries.find(
      (entry) => entry.id === selectedEntry.id,
    );
    if (effective && published) {
      entryAvigilonStatus = (
        <AvigilonStatus
          title="Avigilon entries"
          note={publishedNote}
          lines={[published.sideA, published.sideB].map((side) => ({
            label: `Side ${side.position}`,
            ...avigilonEntrySideLine(
              connectionReadOf(side.id),
              readerAdmitsInto(effective, side),
            ),
          }))}
        />
      );
    } else if (editingDraft) {
      entryAvigilonStatus = (
        <AvigilonStatus
          title="Avigilon entries"
          lines={[selectedEntry.sideA, selectedEntry.sideB].map((side) => ({
            label: `Side ${side.position}`,
            ...draftEntrySideLine(
              zonesModeRead,
              readerAdmitsInto(layout, side),
            ),
          }))}
        />
      );
    }
  }

  const layoutChecks = editingDraft
    ? publishChecks(
        layout,
        (siteReaders ?? []).map((reader) => ({
          id: reader.id,
          name: reader.name,
          missing: reader.missing,
          notConnected: reader.notConnected,
        })),
      )
    : { blocking: [], warnings: [], canPublish: false };
  // Without the site's readers, the unplaced-readers check cannot run; say so
  // rather than report none.
  const checks =
    editingDraft && !placeableReaders
      ? {
          ...layoutChecks,
          warnings: [
            ...layoutChecks.warnings,
            {
              text: "The site's readers have not loaded, so readers not placed on any entry cannot be checked.",
            },
          ],
        }
      : layoutChecks;

  if (layoutError) return <LoadingError error={layoutError.message} />;
  if (!state) return <LoadingError loading />;

  return (
    <>
      <div className={classes.container}>
        <LayoutHeader
          revisionOptions={revisionOptions}
          revisionValue={revisionValue}
          onRevisionChange={onRevisionChange}
          isDraft={isDraft}
          editingDraft={editingDraft}
          showEdit={!isDraft && !viewingRevision}
          busy={busy}
          stalled={stalled}
          onEditLayout={editLayout}
          onOpenDiscard={() => setOpenModal('discard')}
          onOpenPublish={() => setOpenModal('publish')}
          banner={banner}
        />

        <div className={classes.body}>
          <LayoutSidebar
            readerStripText={readerStripText}
            avigilon={
              <AvigilonSyncStatus
                hasPublishedLayout={effective !== null}
                read={shownPublicationRead}
                busy={busy || stalled}
                onSync={syncNow}
              />
            }
            zones={{
              count: layout.zones.length,
              canAdd: canAddZone && !busy && !stalled,
              onAdd: addZone,
              children: layout.zones.map((zone) => (
                <LayoutEntityCard
                  key={zone.id}
                  title={zone.name}
                  subtitle={pluralize(
                    entriesFacingZone(layout, zone.id).length,
                    'entry',
                    'entries',
                  )}
                  tag={
                    editingDraft
                      ? draftTagBadge(draftTagOf(effective, 'zones', zone))
                      : null
                  }
                  selected={selectedZone?.id === zone.id}
                  onClick={() => select('zone', zone.id)}
                />
              )),
            }}
            entries={{
              count: layout.entries.length,
              canAdd: canAddEntry && !busy && !stalled,
              addTitle: layout.zones.length ? undefined : 'Add a zone first',
              onAdd: addEntry,
              children: layout.entries.map((entry) => (
                <LayoutEntityCard
                  key={entry.id}
                  title={entry.name}
                  subtitle={`${entryJoinLabel(layout, entry)} · ${pluralize(
                    layout.readerAssignments.filter(
                      (assignment) => assignment.entryId === entry.id,
                    ).length,
                    'reader',
                  )}`}
                  tag={
                    editingDraft
                      ? draftTagBadge(draftTagOf(effective, 'entries', entry))
                      : null
                  }
                  selected={selectedEntry?.id === entry.id}
                  onClick={() => select('entry', entry.id)}
                />
              )),
            }}
            removedNames={removedNames}
          />

          <section className={classes.main} aria-label="Selected layout item">
            {isEmptyLayout && (
              <div className={classes.emptyLayout}>
                <p className={classes.empty}>
                  This jobsite has no layout yet. Add your first zone.
                </p>
                <Button
                  text="Add zone"
                  size="sm"
                  disabled={busy || stalled}
                  onClick={addZone}
                />
              </div>
            )}
            {!isEmptyLayout && !selectedZone && !selectedEntry && (
              <p className={classes.empty}>Select a zone or an entry.</p>
            )}
            {selectedZone && (
              <ZoneEditor
                layout={layout}
                zone={selectedZone}
                tag={
                  editingDraft
                    ? draftTagOf(effective, 'zones', selectedZone)
                    : null
                }
                readOnly={readOnly}
                editingDraft={editingDraft}
                avigilonStatus={zoneAvigilonStatus}
                onRename={(name) =>
                  run(async (): Promise<void> => {
                    await actions.renameZone(selectedZone.id, name);
                  })
                }
                onSelectEntry={(entryId) => select('entry', entryId)}
                onRemove={() => removeZone(selectedZone.id)}
              />
            )}
            {selectedEntry && (
              <EntryEditor
                layout={layout}
                entry={selectedEntry}
                tag={
                  editingDraft
                    ? draftTagOf(effective, 'entries', selectedEntry)
                    : null
                }
                readOnly={readOnly}
                editingDraft={editingDraft}
                avigilonStatus={entryAvigilonStatus}
                readersById={readersById}
                pickerSideId={pickerSideId}
                onPickerSideChange={setPickerSideId}
                onRename={(name) =>
                  run(async (): Promise<void> => {
                    await actions.renameEntry(selectedEntry.id, name);
                  })
                }
                onSetFaces={(side, value) =>
                  setFaces(selectedEntry.id, side, value)
                }
                onSetEnforcement={(side, enforcement) =>
                  run(async (): Promise<void> => {
                    await actions.updateEntrySide(selectedEntry.id, side.id, {
                      enforcement,
                    });
                  })
                }
                onPlaceReader={(side, readerDeviceId) =>
                  placeReader(selectedEntry.id, side, readerDeviceId)
                }
                onRemoveReader={(readerDeviceId) =>
                  run(() => actions.unassignReader(readerDeviceId))
                }
                onRemove={() =>
                  removeEntry(selectedEntry.id, selectedEntry.name)
                }
              />
            )}
          </section>
        </div>
      </div>

      <PublishDraftModal
        isOpen={openModal === 'publish'}
        siteName={jobsiteName ?? ''}
        nextRevisionNumber={(effective?.number ?? 0) + 1}
        currentRevisionNumber={effective?.number ?? null}
        checks={checks}
        publishing={busy}
        onCancel={() => setOpenModal(null)}
        onPublish={publish}
        onOpenItem={(type, id) => {
          setOpenModal(null);
          select(type, id);
        }}
      />
      <DiscardDraftModal
        isOpen={openModal === 'discard'}
        discarding={busy}
        onCancel={() => setOpenModal(null)}
        onDiscard={discard}
      />
    </>
  );
}
